import { useEffect, useState } from 'react';
import { Icon } from '@iconify-icon/react';

import { useScrollReveal } from './_hooks';

/* ── Live conversation preview ─────────────────────────
   Animated chat showing the kind of answer they'll get
   on the call. Streaming text reveal + citation chips
   that pop in. ────────────────────────────────────── */

const FULL_ANSWER =
  'Yes — under the standard MSA, EU customer data must remain inside the EU. Section 7.3 specifies ' +
  "Frankfurt as the primary region with Dublin as failover. There's also a 30-day deletion SLA after " +
  'contract termination.';

const CITATIONS = [
  { id: '7.3', label: 'MSA · §7.3 Data Residency' },
  { id: '12.1', label: 'DPA · §12.1 Retention' },
  { id: 'A.4', label: 'Annex A.4 · Sub-processors' },
];

function useTypewriter(text: string, active: boolean, speed = 16) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    if (idx >= text.length) return undefined;
    const t = setTimeout(() => setIdx((i) => i + 1), speed);
    return () => clearTimeout(t);
  }, [idx, active, text, speed]);

  useEffect(() => {
    if (active && idx === 0) {
      const t = setTimeout(() => setIdx(1), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [active, idx]);

  return text.slice(0, idx);
}

const DemoLivePreview: React.FC = () => {
  const [ref, visible] = useScrollReveal(0.3);
  const typed = useTypewriter(FULL_ANSWER, visible, 14);
  const done = typed.length === FULL_ANSWER.length;

  return (
    <section className="relative overflow-hidden py-24 sm:py-32" style={{ background: '#0a1024' }}>
      {/* Texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 top-1/4 h-[500px] w-[500px] rounded-full opacity-30 blur-[140px]"
        style={{ background: 'radial-gradient(circle, #004790 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 -left-10 h-[400px] w-[400px] rounded-full opacity-20 blur-[120px]"
        style={{ background: 'radial-gradient(circle, #c08838 0%, transparent 70%)' }}
      />

      <div ref={ref} className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-16">
          {/* ── Left copy ── */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="inline-block h-px w-8 bg-white/30" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/50">
                What an answer looks like
              </span>
            </div>
            <h2
              className="mt-6 text-[40px] leading-[1.02] tracking-tight text-white sm:text-[52px]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 400 }}>
              Citations, not{' '}
              <span
                style={{
                  fontFamily: 'Fraunces, serif',
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontVariationSettings: '"SOFT" 100, "opsz" 144',
                  color: '#f3e4c4',
                }}>
                hallucinations.
              </span>
            </h2>
            <p className="mt-6 max-w-md text-[16px] leading-relaxed text-white/70">
              Every claim our agents make traces back to a paragraph in your documents. No
              confabulated case law, no invented contract terms, no &ldquo;according to
              <span className="italic"> general knowledge</span>.&rdquo;
            </p>

            <ul className="mt-8 space-y-4">
              {[
                'Source-pinned to the exact paragraph',
                'Falls back to "I don\'t know" when unsure',
                'Reviewable answer logs for compliance',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Icon
                    icon="solar:check-circle-bold"
                    className="mt-0.5 text-[20px] text-emerald-400"
                  />
                  <span className="text-[15px] text-white/80">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Right chat card ── */}
          <div className="lg:col-span-7">
            <div
              className="relative rounded-[28px] bg-white/[0.04] p-2 backdrop-blur-md"
              style={{
                boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 40px 80px -20px rgba(0,0,0,0.6)',
              }}>
              {/* Window chrome */}
              <div className="flex items-center justify-between rounded-t-2xl bg-white/[0.06] px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                  </div>
                  <span className="ml-3 text-[11px] font-medium uppercase tracking-[0.18em] text-white/40">
                    counsel.acme.com
                  </span>
                </div>
                <div className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                    Claude · live
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="rounded-b-2xl bg-[#0f1730]/60 px-5 py-6 sm:px-7 sm:py-8">
                {/* User question */}
                <div
                  className="ml-auto max-w-[80%]"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(8px)',
                    transition: 'all 0.5s ease',
                  }}>
                  <div className="rounded-2xl rounded-br-md bg-[#004790] px-4 py-3 text-[14.5px] leading-relaxed text-white">
                    Which clause in our MSA covers data residency for our EU customers?
                  </div>
                  <div className="mt-1.5 text-right text-[10px] font-medium uppercase tracking-wider text-white/30">
                    you · 2:13 PM
                  </div>
                </div>

                {/* Agent response */}
                <div
                  className="mt-7 max-w-[88%]"
                  style={{
                    opacity: visible ? 1 : 0,
                    transition: 'opacity 0.5s ease',
                    transitionDelay: '0.4s',
                  }}>
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-8 w-8 flex-none items-center justify-center rounded-xl"
                      style={{
                        background: 'linear-gradient(135deg, #004790 0%, #003366 100%)',
                      }}>
                      <span
                        className="text-[11px] font-bold text-white"
                        style={{ fontFamily: 'Fraunces, serif' }}>
                        co
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="rounded-2xl rounded-tl-md bg-white/[0.06] px-4 py-3.5 text-[14.5px] leading-[1.65] text-white/90">
                        {typed.split(/(Section 7\.3|Frankfurt|Dublin|30-day)/g).map((part, i) =>
                          ['Section 7.3', 'Frankfurt', 'Dublin', '30-day'].includes(part) ? (
                            <span
                              key={i}
                              className="cursor-help rounded px-0.5"
                              style={{
                                background: 'rgba(192,136,56,0.18)',
                                color: '#f3e4c4',
                                borderBottom: '1px dashed rgba(243,228,196,0.5)',
                              }}>
                              {part}
                            </span>
                          ) : (
                            <span key={i}>{part}</span>
                          )
                        )}
                        {!done && (
                          <span
                            className="ml-0.5 inline-block h-4 w-0.5 align-middle"
                            style={{
                              background: '#f3e4c4',
                              animation: 'caret-blink 0.9s steps(2) infinite',
                            }}
                          />
                        )}
                      </div>

                      {/* Citation chips — appear after stream finishes */}
                      <div
                        className="mt-3 flex flex-wrap gap-2"
                        style={{
                          opacity: done ? 1 : 0,
                          transform: done ? 'translateY(0)' : 'translateY(6px)',
                          transition: 'all 0.5s ease',
                        }}>
                        {CITATIONS.map((c, i) => (
                          <span
                            key={c.id}
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-medium text-white/80 hover:border-white/20 hover:bg-white/[0.08]"
                            style={{
                              transitionDelay: `${i * 80}ms`,
                              animation: done
                                ? `cite-pop 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 0.08}s both`
                                : 'none',
                            }}>
                            <span
                              className="flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold"
                              style={{ background: 'rgba(192,136,56,0.3)', color: '#f3e4c4' }}>
                              {i + 1}
                            </span>
                            {c.label}
                            <Icon icon="solar:arrow-right-up-linear" className="text-[12px]" />
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stream meta */}
                <div className="mt-6 flex items-center justify-between border-t border-white/[0.06] pt-4">
                  <div className="flex items-center gap-3 text-[10px] font-medium uppercase tracking-wider text-white/35">
                    <span>Example response</span>
                    <span className="h-px w-3 bg-white/15" />
                    <span>3 sources</span>
                    <span className="h-px w-3 bg-white/15" />
                    <span>Source citations</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3].map((b) => (
                      <span
                        key={b}
                        className="h-1 w-1 rounded-full bg-white/40"
                        style={{
                          animation: 'dot-fade 1.4s ease-in-out infinite',
                          animationDelay: `${b * 0.2}s`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes caret-blink {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        @keyframes cite-pop {
          from {
            opacity: 0;
            transform: translateY(6px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes dot-fade {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.4); }
        }
      `}</style>
    </section>
  );
};

export default DemoLivePreview;
